import type { LayersList } from '@deck.gl/core'
import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { useMap } from '@vis.gl/react-google-maps'
import { useEffect, useRef } from 'react'

interface IDeckglOverlayProps {
  layers: LayersList
  mapId?: string
}

export const DeckGlOverlay = ({ layers, mapId }: IDeckglOverlayProps): null => {
  const deckRef = useRef<GoogleMapsOverlay | null>(null)
  const map = useMap()
  const mapIdRef = useRef<string | undefined>(mapId)

  // Create or recreate the overlay when mapId changes
  useEffect(() => {
    if (!map) return

    // If mapId changed or there's no existing overlay, create a new one
    if (mapIdRef.current !== mapId || !deckRef.current) {
      // Clean up existing overlay
      if (deckRef.current) {
        deckRef.current.finalize()
      }

      // Create new overlay
      try {
        const deck = new GoogleMapsOverlay({ interleaved: true })
        deck.setMap(map)
        deckRef.current = deck
        mapIdRef.current = mapId
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error initializing DeckGL overlay:', error)
      }
    }

    return () => {
      // Cleanup when component unmounts
      if (deckRef.current) {
        deckRef.current.finalize()
        deckRef.current = null
      }
    }
  }, [map, mapId])

  // Update layers when they change
  useEffect(() => {
    if (deckRef.current && layers && layers.length > 0) {
      try {
        deckRef.current.setProps({ layers })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error updating DeckGL layers:', error)
      }
    }
  }, [layers])

  return null
}
